import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Save, Search, Filter, RotateCcw, Upload, Database } from 'lucide-react';
import '../styles/TranslationsEditor.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TranslationsEditor = ({ token }) => {
  const [translations, setTranslations] = useState([]);
  const [filteredTranslations, setFilteredTranslations] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [versions, setVersions] = useState([]);
  const [editingCell, setEditingCell] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);

  useEffect(() => {
    fetchTranslations();
    fetchVersions();
  }, []);

  useEffect(() => {
    filterTranslations();
  }, [translations, searchTerm, categoryFilter]);

  const fetchTranslations = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/translations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTranslations(response.data.translations || []);
    } catch (error) {
      console.error('Error fetching translations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchVersions = async () => {
    try {
      const response = await axios.get(`${API}/admin/translations/versions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVersions(response.data.versions || []);
    } catch (error) {
      console.error('Error fetching versions:', error);
    }
  };

  const filterTranslations = () => {
    let filtered = [...translations];

    if (searchTerm) {
      filtered = filtered.filter(t =>
        t.key.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.de.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.en.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.es.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (categoryFilter !== 'all') {
      filtered = filtered.filter(t => t.category === categoryFilter);
    }

    setFilteredTranslations(filtered);
  };

  const getCategories = () => {
    const categories = [...new Set(translations.map(t => t.category))];
    return ['all', ...categories];
  };

  const handleCellEdit = (translationId, field, value) => {
    setTranslations(prev => prev.map(t =>
      t.id === translationId ? { ...t, [field]: value, status: 'draft' } : t
    ));
    setHasChanges(true);
  };

  const saveDraft = async () => {
    try {
      setLoading(true);
      for (const trans of translations.filter(t => t.status === 'draft')) {
        await axios.put(`${API}/admin/translations/${trans.id}`, trans, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      alert('Entwurf erfolgreich gespeichert!');
      setHasChanges(false);
    } catch (error) {
      console.error('Error saving draft:', error);
      alert('Fehler beim Speichern des Entwurfs');
    } finally {
      setLoading(false);
    }
  };

  const publishChanges = async () => {
    if (!window.confirm('Möchten Sie alle Änderungen live schalten? Dies erstellt automatisch ein Backup.')) {
      return;
    }

    try {
      setLoading(true);
      await saveDraft();
      await axios.post(`${API}/admin/translations/publish`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchTranslations();
      await fetchVersions();
      alert('Änderungen erfolgreich live geschaltet!');
      setHasChanges(false);
    } catch (error) {
      console.error('Error publishing changes:', error);
      alert('Fehler beim Live-Schalten der Änderungen');
    } finally {
      setLoading(false);
    }
  };

  const rollbackToVersion = async (versionId) => {
    if (!window.confirm('Möchten Sie wirklich zu dieser Version zurückkehren? Alle aktuellen Änderungen gehen verloren.')) {
      return;
    }

    try {
      setLoading(true);
      await axios.post(`${API}/admin/translations/rollback/${versionId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchTranslations();
      alert('Erfolgreich zur ausgewählten Version zurückgekehrt!');
      setHasChanges(false);
    } catch (error) {
      console.error('Error rolling back:', error);
      alert('Fehler beim Zurückkehren zur Version');
    } finally {
      setLoading(false);
    }
  };

 // Initialize translations from default data
  const initializeTranslations = async () => {
    if (!window.confirm('Möchten Sie die Standard-Übersetzungen neu laden? Dies ersetzt ALLE bestehenden Übersetzungen mit den Standardwerten (inkl. Destinations und Kontinente).')) {
      return;
    }

    try {
      setIsInitializing(true);
      const response = await axios.post(`${API}/admin/translations/init`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert(`Übersetzungen erfolgreich neu geladen! ${response.data.count || ''} Einträge wurden erstellt.`);
      await fetchTranslations();
      await fetchVersions();
    } catch (error) {
      console.error('Error initializing translations:', error);
      alert('Fehler beim Laden der Übersetzungen: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIsInitializing(false);
    }
  };

  const groupedTranslations = {};
  filteredTranslations.forEach(t => {
    if (!groupedTranslations[t.category]) {
      groupedTranslations[t.category] = [];
    }
    groupedTranslations[t.category].push(t);
  });

  // Show initialization UI if no translations exist
  if (!loading && translations.length === 0) {
    return (
      <div className="translations-editor">
        <div className="editor-header">
          <h2>Übersetzungsverwaltung</h2>
        </div>
        <div style={{
          padding: '60px 40px',
          textAlign: 'center',
          backgroundColor: '#f9fafb',
          borderRadius: '12px',
          margin: '20px 0'
        }}>
          <Database size={64} style={{ color: '#c084fc', marginBottom: '20px' }} />
          <h3 style={{ marginBottom: '12px', color: '#374151' }}>Keine Übersetzungen vorhanden</h3>
          <p style={{ color: '#6b7280', marginBottom: '24px' }}>
            Die Übersetzungsdatenbank ist leer. Klicken Sie auf den Button unten, um die Standard-Übersetzungen zu initialisieren.
          </p>
          <button
            onClick={initializeTranslations}
            disabled={isInitializing}
            style={{
              padding: '14px 28px',
              backgroundColor: '#c084fc',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: isInitializing ? 'not-allowed' : 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <Database size={20} />
            {isInitializing ? 'Wird initialisiert...' : 'Standard-Übersetzungen laden'}
          </button>
        </div>
      </div>
    );
  }

 return (
    <div className="translations-editor">
      <div className="editor-header">
        <h2>Übersetzungsverwaltung</h2>
        <div className="editor-actions">
          <button 
            onClick={initializeTranslations} 
            className="btn-init" 
            disabled={loading || isInitializing}
            style={{
              padding: '8px 16px',
              backgroundColor: '#8b5cf6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: (loading || isInitializing) ? 'not-allowed' : 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              marginRight: '10px'
            }}
          >
            <Database size={16} />
            {isInitializing ? 'Wird geladen...' : 'Standard-Übersetzungen neu laden'}
          </button>
          {hasChanges && (
            <>
              <button onClick={saveDraft} className="btn-draft" disabled={loading}>
                <Save size={16} />
                Entwurf speichern
              </button>
              <button onClick={publishChanges} className="btn-publish" disabled={loading}>
                <Upload size={16} />
                Live schalten
              </button>
            </>
          )}
        </div>
      </div>

      <div className="editor-controls">
        <div className="search-box">
          <Search size={16} />
          <input
            type="text"
            placeholder="Suche nach Schlüssel oder Text..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="filter-box">
          <Filter size={16} />
          <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
            {getCategories().map(cat => (
              <option key={cat} value={cat}>
                {cat === 'all' ? 'Alle Kategorien' : cat}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="translations-table-container">
        {Object.keys(groupedTranslations).map(category => (
          <div key={category} className="category-group">
            <h3 className="category-title">{category}</h3>
            <table className="translations-table">
              <thead>
                <tr>
                  <th>Schlüssel</th>
                  <th>Deutsch</th>
                  <th>Englisch</th>
                  <th>Spanisch</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {groupedTranslations[category].map(trans => (
                  <tr key={trans.id} className={trans.status === 'draft' ? 'draft-row' : ''}>
                    <td className="key-cell">{trans.key}</td>
                    <td
                      className="editable-cell"
                      onClick={() => setEditingCell({ id: trans.id, field: 'de' })}
                    >
                      {editingCell?.id === trans.id && editingCell?.field === 'de' ? (
                        <input
                          type="text"
                          value={trans.de}
                          onChange={(e) => handleCellEdit(trans.id, 'de', e.target.value)}
                          onBlur={() => setEditingCell(null)}
                          autoFocus
                        />
                      ) : (
                        trans.de
                      )}
                    </td>
                    <td
                      className="editable-cell"
                      onClick={() => setEditingCell({ id: trans.id, field: 'en' })}
                    >
                      {editingCell?.id === trans.id && editingCell?.field === 'en' ? (
                        <input
                          type="text"
                          value={trans.en}
                          onChange={(e) => handleCellEdit(trans.id, 'en', e.target.value)}
                          onBlur={() => setEditingCell(null)}
                          autoFocus
                        />
                      ) : (
                        trans.en
                      )}
                    </td>
                    <td
                      className="editable-cell"
                      onClick={() => setEditingCell({ id: trans.id, field: 'es' })}
                    >
                      {editingCell?.id === trans.id && editingCell?.field === 'es' ? (
                        <input
                          type="text"
                          value={trans.es}
                          onChange={(e) => handleCellEdit(trans.id, 'es', e.target.value)}
                          onBlur={() => setEditingCell(null)}
                          autoFocus
                        />
                      ) : (
                        trans.es
                      )}
                    </td>
                    <td>
                      <span className={`status-badge ${trans.status}`}>{trans.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>

      <div className="versions-panel">
        <h3>Versionen ({versions.length})</h3>
        <div className="versions-list">
          {versions.map(version => (
            <div key={version.id} className="version-item">
              <div className="version-info">
                <strong>{version.version_name}</strong>
                <span className="version-date">
                  {new Date(version.created_at).toLocaleString('de-DE')}
                </span>
              </div>
              <button
                onClick={() => rollbackToVersion(version.id)}
                className="btn-rollback"
                disabled={loading}
              >
                <RotateCcw size={14} />
                Wiederherstellen
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TranslationsEditor;
